import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import useSeo from "@/hooks/useSeo";
import { comics } from "@/mocks/manga";
import { mangaPageImagePool, chapterTitles } from "@/mocks/readerPages";
import { NotFoundPage } from "@/pages/NotFound";

interface Chapter {
  id: string;
  title: string;
  pages: string[];
}

const READER_KEY = "manyue-reading-pref";

function buildChapters(seed: string): Chapter[] {
  const hash = seed.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return chapterTitles.map((t, i) => {
    const pages: string[] = [];
    const count = 6 + (i % 3);
    for (let p = 0; p < count; p++) {
      pages.push(mangaPageImagePool[(hash + i + p) % mangaPageImagePool.length]);
    }
    return { id: `${seed}-${i + 1}`, title: `第 ${i + 1} 话 · ${t}`, pages };
  });
}

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = useMemo(() => comics.find((c) => c.id === id), [id]);
  const chapters = useMemo(() => (comic ? buildChapters(comic.id) : []), [comic]);

  const [activeChapter, setActiveChapter] = useState<string | null>(null);
  const [mode, setMode] = useState<"page" | "scroll">("page");
  const [landscape, setLandscape] = useState(false);
  const [night, setNight] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const [favorited, setFavorited] = useState(false);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(READER_KEY) || "");
      if (saved.mode) setMode(saved.mode);
      if (saved.landscape) setLandscape(saved.landscape);
      if (saved.night) setNight(saved.night);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      READER_KEY,
      JSON.stringify({ mode, landscape, night, chapter: activeChapter, page: currentPage })
    );
  }, [mode, landscape, night, activeChapter, currentPage]);

  useEffect(() => {
    setCurrentPage(0);
  }, [activeChapter]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2200);
  };

  const current = chapters.find((c) => c.id === activeChapter) || null;

  const downloadChapter = () => {
    if (!current) return;
    showToast(`第 ${current.title} 已缓存，可离线观看`);
  };

  const downloadAll = () => {
    showToast("全部章节已加入缓存队列，可离线观看");
  };

  const toggleFav = () => {
    setFavorited((v) => !v);
    showToast(favorited ? "已取消收藏" : "已加入收藏");
  };

  useSeo({
    title: comic ? `${comic.title} - ${comic.categoryName}漫画在线阅读` : "漫画未找到",
    description: comic
      ? `${comic.title}，作者${comic.author}，${comic.status}，评分${comic.rating}。${comic.description}支持翻页模式、横屏阅读、夜间模式与缓存下载。`
      : "未找到该漫画。",
    keywords: `${comic?.title},${comic?.categoryName}漫画,漫画阅读,在线漫画`,
    path: `/comic/${comic?.id || ""}`,
    jsonLd: comic
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Book",
            name: comic.title,
            author: { "@type": "Person", name: comic.author },
            genre: comic.categoryName,
            description: comic.description,
            image: comic.cover,
            url: `/comic/${comic.id}`,
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: comic.rating,
              bestRating: 10,
              ratingCount: Math.round(comic.popularity / 100),
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "/" },
              { "@type": "ListItem", position: 2, name: `${comic.categoryName}漫画`, item: `/categories?cat=${comic.category}` },
              { "@type": "ListItem", position: 3, name: comic.title, item: `/comic/${comic.id}` },
            ],
          },
        ]
      : [],
  });

  if (!comic) {
    return <NotFoundPage />;
  }

  return (
    <Layout>
      {/* 顶部信息 */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
          <nav className="text-xs text-foreground-500" aria-label="面包屑导航">
            <ol className="flex flex-wrap items-center gap-1">
              <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
              <li><i className="ri-arrow-right-s-line" aria-hidden="true" /></li>
              <li>
                <Link to={`/categories?cat=${comic.category}`} className="hover:text-primary-600">
                  {comic.categoryName}漫画
                </Link>
              </li>
              <li><i className="ri-arrow-right-s-line" aria-hidden="true" /></li>
              <li className="font-medium text-foreground-950">{comic.title}</li>
            </ol>
          </nav>

          <div className="mt-6 flex flex-col gap-6 md:flex-row">
            <div className="shrink-0">
              <div className="relative w-40 overflow-hidden rounded-lg border border-background-200 md:w-48">
                <img
                  src={comic.cover}
                  alt={`${comic.title} 漫画封面`}
                  title={`${comic.title} 在线漫画`}
                  className="aspect-[4/5] w-full object-cover object-top"
                />
                <span className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-background-950/70 px-2 py-0.5 text-xs text-background-50">
                  <i className="ri-star-fill text-accent-500" aria-hidden="true" />
                  {comic.rating}
                </span>
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <h1 className="font-heading text-2xl text-foreground-950 md:text-3xl">{comic.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">{comic.status}</span>
                <span className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-700">{comic.categoryName}</span>
                <span className="text-sm text-foreground-500">作者：{comic.author}</span>
                <span className="text-sm text-foreground-500">· 阅读 {comic.views}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {comic.tags.map((t) => (
                  <span key={t} className="rounded-md bg-accent-100 px-2 py-0.5 text-xs text-accent-900">{t}</span>
                ))}
              </div>
              <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600">{comic.description}</p>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setActiveChapter(activeChapter ? activeChapter : chapters[0]?.id || null)}
                  className="inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-book-open-line mr-2" aria-hidden="true" />
                  {activeChapter ? "继续阅读" : "开始阅读"}
                </button>
                <button
                  type="button"
                  onClick={toggleFav}
                  className="inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-background-200 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-300"
                >
                  <i className={`${favorited ? "ri-heart-fill text-primary-500" : "ri-heart-line"} mr-2`} aria-hidden="true" />
                  {favorited ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={downloadAll}
                  className="inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-background-200 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-300"
                >
                  <i className="ri-download-2-line mr-2" aria-hidden="true" />
                  缓存全部
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 章节列表 */}
      {!activeChapter && (
        <section className="bg-background-50 py-10 md:py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">章节列表</h2>
            <p className="mt-1 text-sm text-foreground-500">共 {chapters.length} 话 · 点击任意章节开始阅读</p>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {chapters.map((c, i) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setActiveChapter(c.id)}
                  className="cursor-pointer rounded-md border border-background-200 bg-background-50 px-4 py-3 text-left text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-primary-50"
                >
                  <span className="text-xs text-primary-600">第 {i + 1} 话</span>
                  <span className="mt-0.5 block truncate">{c.title.replace(/^第 \d+ 话 · /, "")}</span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 阅读器 */}
      {activeChapter && current && (
        <section
          className={`py-8 transition-colors ${
            night ? "bg-foreground-950" : "bg-background-950"
          }`}
        >
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            {/* 阅读器工具栏 */}
            <div className="mb-5 flex flex-col gap-3 rounded-lg border border-background-50/10 bg-background-900/70 p-3 backdrop-blur sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveChapter(null)}
                  className="cursor-pointer rounded-md px-2 py-1.5 text-xs text-background-100 hover:bg-background-800"
                >
                  <i className="ri-arrow-left-line mr-1" aria-hidden="true" />
                  章节列表
                </button>
                <span className="truncate text-sm font-medium text-background-50">{current.title}</span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setMode(mode === "page" ? "scroll" : "page")}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    mode === "page" ? "bg-primary-500 text-background-50" : "bg-background-800 text-background-100"
                  }`}
                >
                  <i className="ri-book-open-line mr-1" aria-hidden="true" />
                  翻页模式
                </button>
                <button
                  type="button"
                  onClick={() => setMode(mode === "scroll" ? "page" : "scroll")}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    mode === "scroll" ? "bg-primary-500 text-background-50" : "bg-background-800 text-background-100"
                  }`}
                >
                  <i className="ri-arrow-down-line mr-1" aria-hidden="true" />
                  卷动模式
                </button>
                <button
                  type="button"
                  onClick={() => setLandscape((v) => !v)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    landscape ? "bg-accent-500 text-background-950" : "bg-background-800 text-background-100"
                  }`}
                >
                  <i className="ri-smartphone-line mr-1" aria-hidden="true" />
                  横屏阅读
                </button>
                <button
                  type="button"
                  onClick={() => setNight((v) => !v)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    night ? "bg-secondary-500 text-background-50" : "bg-background-800 text-background-100"
                  }`}
                >
                  <i className="ri-moon-clear-line mr-1" aria-hidden="true" />
                  夜间模式
                </button>
                <button
                  type="button"
                  onClick={downloadChapter}
                  className="cursor-pointer whitespace-nowrap rounded-full bg-background-800 px-3 py-1.5 text-xs font-medium text-background-100 transition-colors hover:bg-background-700"
                >
                  <i className="ri-download-2-line mr-1" aria-hidden="true" />
                  缓存本章
                </button>
              </div>
            </div>

            {/* 阅读内容 */}
            {mode === "page" ? (
              <div className="flex items-start justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className="mt-[40vh] hidden h-12 w-12 cursor-pointer items-center justify-center rounded-full bg-background-800 text-background-100 transition-colors hover:bg-background-700 disabled:cursor-not-allowed disabled:opacity-40 md:flex"
                  aria-label="上一页"
                >
                  <i className="ri-arrow-left-line text-xl" aria-hidden="true" />
                </button>

                <div className={`overflow-hidden rounded-md bg-background-900 ${landscape ? "w-full md:max-w-4xl" : "max-w-2xl"}`}>
                  <img
                    src={current.pages[currentPage]}
                    alt={`${comic.title} ${current.title} 第 ${currentPage + 1} 页`}
                    title={`${comic.title} ${current.title}`}
                    className="w-full object-contain"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(current.pages.length - 1, p + 1))}
                  disabled={currentPage === current.pages.length - 1}
                  className="mt-[40vh] hidden h-12 w-12 cursor-pointer items-center justify-center rounded-full bg-background-800 text-background-100 transition-colors hover:bg-background-700 disabled:cursor-not-allowed disabled:opacity-40 md:flex"
                  aria-label="下一页"
                >
                  <i className="ri-arrow-right-line text-xl" aria-hidden="true" />
                </button>
              </div>
            ) : (
              <div className={`mx-auto overflow-hidden rounded-md bg-background-900 ${landscape ? "md:max-w-4xl" : "max-w-2xl"}`}>
                {current.pages.map((p, i) => (
                  <img
                    key={i}
                    src={p}
                    alt={`${comic.title} ${current.title} 第 ${i + 1} 页`}
                    title={`${comic.title} ${current.title}`}
                    className="w-full object-contain"
                  />
                ))}
              </div>
            )}

            {/* 移动端分页 + 页码 */}
            <div className="mt-5 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                className="inline-flex cursor-pointer items-center whitespace-nowrap rounded-md bg-background-800 px-4 py-2 text-sm text-background-100 transition-colors hover:bg-background-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <i className="ri-arrow-left-line mr-1" aria-hidden="true" />
                上一页
              </button>
              <p className="text-xs text-background-100/70">
                {currentPage + 1} / {current.pages.length} 页
              </p>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(current.pages.length - 1, p + 1))}
                disabled={currentPage === current.pages.length - 1}
                className="inline-flex cursor-pointer items-center whitespace-nowrap rounded-md bg-background-800 px-4 py-2 text-sm text-background-100 transition-colors hover:bg-background-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                下一页
                <i className="ri-arrow-right-line ml-1" aria-hidden="true" />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* 相关推荐 */}
      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="mb-6 flex items-end justify-between">
            <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">猜你喜欢</h2>
            <Link to="/categories" className="text-sm font-medium text-primary-600 hover:text-primary-700">
              查看更多
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {comics
              .filter((c) => c.id !== comic.id && c.category === comic.category)
              .slice(0, 5)
              .map((c) => (
                <Link
                  key={c.id}
                  to={`/comic/${c.id}`}
                  className="group block overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-transform hover:-translate-y-1"
                >
                  <div className="aspect-[4/5] w-full overflow-hidden bg-background-200">
                    <img
                      src={c.cover}
                      alt={`${c.title} 封面`}
                      title={`${c.title} 在线漫画`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-3">
                    <p className="truncate text-sm font-bold text-foreground-950 group-hover:text-primary-600">{c.title}</p>
                    <p className="mt-0.5 text-xs text-foreground-500">{c.latestChapter}</p>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2">
          <div className="flex items-center gap-2 rounded-full bg-background-950 px-5 py-2.5 text-sm text-background-50 shadow-lg">
            <i className="ri-checkbox-circle-line text-accent-500" aria-hidden="true" />
            {toast}
          </div>
        </div>
      )}
    </Layout>
  );
}